/* Ombrail assistant (v20) — one assistant for the whole site.
   "The right question at the right time": contextual questions on product pages, the piece pinned at
   the top with its next step, quick replies after each answer, and suggested pieces that can be added
   to the cart when they have a fixed price. The conversation follows the visitor between pages
   (sessionStorage). If the AI backend is unavailable, instant answers come from the page data. */
(() => {
  const root = document.querySelector('[data-o-ai]');
  if (!root) return;
  const $ = (sel, scope = root) => scope.querySelector(sel);
  const d = root.dataset;
  const endpoint = (d.endpoint || '').replace(/\/$/, '');
  const shopRoot = window.Shopify?.routes?.root || '/';
  const launcher = $('[data-o-ai-launcher]');
  const panel = $('[data-o-ai-panel]');
  const scrim = $('[data-o-ai-scrim]');
  const thread = $('[data-o-ai-thread]');
  const welcome = $('[data-o-ai-welcome]');
  const form = $('[data-o-ai-form]');
  const input = $('[data-o-ai-input]');
  const statusLine = $('[data-o-ai-status]');
  const cta = $('[data-o-ai-cta]');
  const isProduct = 'productPage' in d;
  const KEY = 'ombrail.ai.v2';
  const mobile = window.matchMedia('(max-width: 700px)');

  let state = { entries: [], context: '' };
  let aiReady = endpoint ? null : false;
  let aiCheck = null;
  let busy = false;
  let lastOpener = null;

  /* ---------- storage ---------- */
  const save = () => {
    try { sessionStorage.setItem(KEY, JSON.stringify({ entries: state.entries.slice(-24), context: state.context })); } catch (e) { /* private mode */ }
  };
  const load = () => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (raw && Array.isArray(raw.entries)) state = { entries: raw.entries, context: raw.context || '' };
    } catch (e) { /* ignore */ }
  };

  /* ---------- open / close ---------- */
  const isOpen = () => !panel.hidden;
  const lock = (on) => document.documentElement.classList.toggle('o-ai-lock', on && mobile.matches);
  let rendered = false;
  const ensureRendered = () => { if (!rendered) { rendered = true; renderAll(); } };
  const open = (from) => {
    if (from) lastOpener = from;
    ensureRendered();
    panel.hidden = false;
    scrim.hidden = false;
    root.classList.add('is-open');
    root.classList.remove('is-tucked');
    hideTeaser();
    launcher.setAttribute('aria-expanded', 'true');
    lock(true);
    checkAI();
    warm();
    requestAnimationFrame(() => { thread.scrollTop = thread.scrollHeight; });
    if (!mobile.matches) input.focus({ preventScroll: true });
  };
  const close = () => {
    if (!isOpen()) return;
    panel.hidden = true;
    scrim.hidden = true;
    root.classList.remove('is-open');
    launcher.setAttribute('aria-expanded', 'false');
    lock(false);
    syncTuck();
    (lastOpener && lastOpener.isConnected ? lastOpener : launcher).focus({ preventScroll: true });
    lastOpener = null;
  };
  window.OmbrailAI = { open, close, ask: (q, topic) => ask(q, topic), isOpen };

  // Loads the catalogue on the server while the visitor reads the welcome, so the first answer is faster.
  let warmed = false;
  function warm() {
    if (warmed || !endpoint) return;
    warmed = true;
    fetch(endpoint + '/chat?warm', { mode: 'cors', keepalive: true }).catch(() => {});
  }

  /* ---------- AI availability ---------- */
  function checkAI() {
    if (aiReady !== null) return Promise.resolve(aiReady);
    if (aiCheck) return aiCheck;
    aiCheck = fetch(endpoint + '/health', { mode: 'cors' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => { aiReady = Boolean(data && data.ai); })
      .catch(() => { aiReady = false; })
      .then(() => {
        if (statusLine) statusLine.textContent = aiReady ? 'AI assistant · replies in a few seconds' : (isProduct ? 'Instant answers from this piece\'s details' : 'Our team replies by email');
        return aiReady;
      });
    return aiCheck;
  }

  /* ---------- rendering ---------- */
  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const paragraphs = (node, text) => {
    String(text || '').replace(/\*\*/g, '').split(/\n{2,}/).map((t) => t.trim()).filter(Boolean).forEach((t) => {
      const lines = t.split(/\n/);
      const p = el('p');
      lines.forEach((line, i) => { if (i) p.append(document.createElement('br')); p.append(line.replace(/^[-•]\s*/, '• ')); });
      node.append(p);
    });
  };
  const sameOriginPath = (url) => {
    try {
      const u = new URL(url, location.origin);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      return u.pathname + (preview ? '?preview_theme_id=' + encodeURIComponent(preview) : '');
    } catch (e) { return ''; }
  };
  const contactUrl = (text) => shopRoot + 'pages/contact?request=question' + (d.handle ? '&piece=' + encodeURIComponent(d.handle) : '') + (text ? '&message=' + encodeURIComponent(String(text).slice(0, 300)) : '');
  const scrollDown = () => { thread.scrollTop = thread.scrollHeight; };

  const sendToTeam = (summary) => {
    if (typeof window.OmbrailOpenQuote === 'function') {
      close();
      window.OmbrailOpenQuote('question', summary || '');
      return;
    }
    location.assign(contactUrl(summary));
  };

  const updateCartCount = async () => {
    try {
      const cart = await fetch(shopRoot + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.querySelectorAll('.o-head__cart').forEach((link) => {
        const badge = link.querySelector('b');
        if (badge) badge.textContent = cart.item_count;
        link.setAttribute('aria-label', (link.getAttribute('aria-label') || 'Cart').replace(/:\s*\d+$/, ': ' + cart.item_count));
      });
    } catch (e) { /* the count refreshes on the next page */ }
  };
  const addToCart = async (variantId, quantity, button, note) => {
    if (!variantId || button.disabled) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const res = await fetch(shopRoot + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) }] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'This piece could not be added.');
      button.textContent = 'Added ✓';
      button.classList.add('is-done');
      if (note) {
        note.hidden = false;
        note.textContent = '';
        note.append('In your cart. ');
        const a = el('a', '', 'View cart →');
        a.href = shopRoot + 'cart';
        note.append(a);
      }
      updateCartCount();
    } catch (err) {
      button.disabled = false;
      button.textContent = label;
      if (note) { note.hidden = false; note.textContent = err.message || 'This piece could not be added.'; }
    }
  };

  const productCards = (products) => {
    const list = el('div', 'o-ai__cards');
    products.slice(0, 3).forEach((item) => {
      const path = sameOriginPath(item.url);
      if (!path) return;
      const card = el('article', 'o-ai__card');
      const link = el('a', 'o-ai__card-link');
      link.href = path;
      if (item.image) {
        const img = el('img');
        img.src = String(item.image).replace(/^http:/, 'https:') + (String(item.image).includes('?') ? '&' : '?') + 'width=240';
        img.alt = ''; img.width = 120; img.height = 120; img.loading = 'lazy';
        link.append(img);
      }
      link.append(el('strong', '', item.title || ''));
      link.append(el('span', 'o-ai__card-price', item.price || ''));
      card.append(link);
      const actions = el('div', 'o-ai__card-actions');
      if (item.variantId) {
        const add = el('button', 'o-ai__card-add', 'Add to cart');
        add.type = 'button';
        const note = el('p', 'o-ai__card-note');
        note.hidden = true;
        add.addEventListener('click', () => addToCart(item.variantId, 1, add, note));
        actions.append(add);
        card.append(actions, note);
      } else {
        const view = el('a', 'o-ai__card-view', item.checkout ? 'See options' : 'View the piece');
        view.href = path;
        actions.append(view);
        card.append(actions);
      }
      list.append(card);
    });
    return list;
  };

  const feedbackRow = (entry, index) => {
    const row = el('div', 'o-ai__rate');
    if (entry.vote) { row.append(el('span', '', 'Thanks for your feedback.')); return row; }
    row.append(el('span', '', 'Helpful?'));
    [['up', 'Yes, this answer helped', 'M7 11v9H4v-9zM7 11l4-7c1.5 0 2.5 1 2.2 2.6L12.6 10H18a2 2 0 0 1 2 2.3l-1.1 6A2 2 0 0 1 17 20H7'],
      ['down', 'No, this answer did not help', 'M17 13V4h3v9zM17 13l-4 7c-1.5 0-2.5-1-2.2-2.6l.6-3.4H6a2 2 0 0 1-2-2.3l1.1-6A2 2 0 0 1 7 4h10']].forEach(([vote, label, path]) => {
      const b = el('button', 'o-ai__rate-btn');
      b.type = 'button';
      b.setAttribute('aria-label', label);
      b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + path + '"/></svg>';
      b.addEventListener('click', () => {
        entry.vote = vote;
        save();
        const previous = state.entries[index - 1];
        if (endpoint) {
          fetch(endpoint + '/feedback', {
            method: 'POST', mode: 'cors', keepalive: true, headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ vote, page: location.pathname, question: previous && previous.role === 'user' ? previous.text : '', reply: String(entry.text || '').slice(0, 1200) }),
          }).catch(() => {});
        }
        row.replaceWith(feedbackRow(entry, index));
      });
      row.append(b);
    });
    return row;
  };

  const chipRow = (list, cls) => {
    const row = el('div', cls || 'o-ai__quick');
    list.forEach((item) => {
      const text = typeof item === 'string' ? item : item.text;
      const b = el('button', '', text);
      b.type = 'button';
      b.dataset.oAiAsk = text;
      if (item.topic) b.dataset.oAiTopic = item.topic;
      row.append(b);
    });
    return row;
  };

  const productQuestions = () => Array.from(welcome.querySelectorAll('[data-o-ai-ask]')).map((b) => ({ text: b.dataset.oAiAsk, topic: b.dataset.oAiTopic || '' }));

  const renderEntry = (entry, index, isLast) => {
    if (entry.role === 'context') {
      const box = el('div', 'o-ai__context');
      box.append(el('span', '', 'Now looking at'), el('strong', '', entry.text));
      if (isLast && isProduct && entry.text === d.product) box.append(chipRow(productQuestions()));
      thread.append(box);
      return;
    }
    if (entry.role === 'user') {
      const b = el('div', 'o-ai__msg o-ai__msg--you');
      paragraphs(b, entry.text);
      thread.append(b);
      return;
    }
    const b = el('div', 'o-ai__msg o-ai__msg--ai');
    paragraphs(b, entry.text);
    if (Array.isArray(entry.products) && entry.products.length) b.append(productCards(entry.products));
    if (entry.handoff) {
      const t = el('button', 'o-ai__handoff', 'Send this question to our team');
      t.type = 'button';
      t.addEventListener('click', () => sendToTeam(entry.handoff));
      b.append(t);
    }
    if (entry.ai) b.append(feedbackRow(entry, index));
    thread.append(b);
    if (isLast && Array.isArray(entry.suggestions) && entry.suggestions.length) thread.append(chipRow(entry.suggestions));
  };

  const renderAll = () => {
    Array.from(thread.children).forEach((child) => { if (child !== welcome) child.remove(); });
    welcome.classList.toggle('is-compact', state.entries.length > 0);
    state.entries.forEach((entry, i) => renderEntry(entry, i, i === state.entries.length - 1));
    scrollDown();
  };

  const push = (entry) => {
    thread.querySelectorAll('.o-ai__quick').forEach((row) => { if (!welcome.contains(row)) row.remove(); });
    state.entries.push(entry);
    welcome.classList.add('is-compact');
    renderEntry(entry, state.entries.length - 1, true);
    save();
    scrollDown();
  };

  /* ---------- instant answers (no AI) ---------- */
  const priceAnswer = () => {
    switch (d.priceStatus) {
      case 'checkout': return 'The size and stone shown have a fixed price, next to the title: add it to your cart and pay at checkout. For another size or stone, ask for a quote.';
      case 'indicative': return 'The price shown is indicative for the reference size and stone. Request a quote to confirm the final price; nothing is made before you approve it.';
      case 'review': return 'This piece is presented for inspiration while its specification is reviewed. Ask our team about availability.';
      default: return 'This piece is priced on request, for its size, stone and your delivery address. The quote is free and without obligation, and you receive it by email.';
    }
  };
  const topicAnswer = (topic) => {
    if (!isProduct) return null;
    switch (topic) {
      case 'fit': return d.dimensions;
      case 'stone': return d.material;
      case 'buy': return d.priceStatus === 'checkout' ? d.production + ' ' + d.delivery : (d.customizable === 'true' ? 'You can ask our team about another size or stone. They confirm what is possible and its price in a free quote.' : priceAnswer());
      default: return null;
    }
  };
  const localAnswer = (question) => {
    if (!isProduct) return null;
    const n = question.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');
    if (/custom|different stone|different size|another size|another stone|bespoke|made to measure|sur mesure|personnalis/.test(n)) return d.customizable === 'true' ? 'You can ask our team about another size or stone. They confirm what is possible and its price.' : 'Please ask our team whether another configuration is available for this piece.';
    if (/door|stair|lift|elevator|corridor|entrance|access|fit through|porte|escalier|ascenseur|passage|goes well|match|pair/.test(n)) return null;
    if (/dimension|size|height|width|length|depth|fit|measure|sofa|seat|people|largeur|longueur|hauteur|taille|cm\b/.test(n)) return d.dimensions;
    if (/marble|travertine|onyx|limestone|sintered|stone|material|vein|colou?r|stain|care|clean|durable|marbre|pierre|matiere|couleur|veine/.test(n)) return d.material;
    if (/deliver|shipping|ship|arrive|transport|livrai|expedi/.test(n)) return d.delivery;
    if (/production|lead time|when|ready|how long|dela[iy]|fabrication|disponib/.test(n)) return d.production;
    if (/price|cost|budget|checkout|cart|order|purchase|buy|quote|prix|cout|commander|acheter|panier|devis/.test(n)) return priceAnswer();
    return null;
  };
  const offlineReply = (question, topic, reason) => {
    const answer = topicAnswer(topic) || localAnswer(question);
    if (answer) { push({ role: 'assistant', text: answer }); return; }
    const text = reason === 'rate_limited'
      ? 'You have asked a lot of questions in a short time. Please wait a few minutes, or send your question to our team.'
      : (isProduct ? 'I don\'t have a verified answer to that in this piece\'s details. Our team can answer by email.' : 'I can\'t answer right now. Our team can reply to your question by email.');
    push({ role: 'assistant', text, handoff: question });
  };

  /* ---------- asking ---------- */
  const pagePayload = () => ({ type: d.pageType || '', title: d.pageTitle || '', terms: d.pageTerms || '' });
  const productPayload = () => ({
    handle: d.handle || '',
    title: d.product || '',
    facts: {
      reference: d.reference || '', price: d.priceText || '', priceStatus: d.priceStatus || '',
      dimensions: d.rawDimensions || '', material: d.rawMaterial || '', production: d.rawProduction || '',
      delivery: d.delivery || '', customizable: d.customizable || '',
    },
  });

  const setBusy = (on) => {
    busy = on;
    root.classList.toggle('is-thinking', on);
    form.querySelector('button[type="submit"]').disabled = on;
  };

  // Streamed answer: the text appears as it is written; cards and buttons come with the final line.
  async function readStream(res, typing, typingText, timers) {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let text = '';
    let live = null;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let cut;
      while ((cut = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, cut).trim();
        buffer = buffer.slice(cut + 1);
        if (!line) continue;
        let ev;
        try { ev = JSON.parse(line); } catch (e) { continue; }
        if (ev.type === 'done') return ev;
        if (ev.type === 'error') return { error: ev.error };
        if (ev.type === 'status' && !live) { timers.forEach(clearTimeout); typingText.textContent = ev.text === 'searching' ? 'Looking through the collection\u2026' : 'Checking the details\u2026'; }
        if (ev.type === 'round') text = '';
        if (ev.type === 'delta') {
          text += ev.text;
          if (!live) {
            timers.forEach(clearTimeout);
            typing.hidden = true;
            live = el('div', 'o-ai__msg o-ai__msg--ai o-ai__live');
            thread.append(live);
          }
          live.textContent = text.replace(/\*\*/g, '');
          scrollDown();
        }
      }
    }
    return {};
  }

  async function ask(question, topic) {
    const q = String(question || '').trim().slice(0, 300);
    if (!q || busy) return;
    if (!isOpen()) open();
    push({ role: 'user', text: q });
    await checkAI();
    if (!aiReady) { offlineReply(q, topic); return; }
    const typing = el('div', 'o-ai__msg o-ai__msg--ai o-ai__typing');
    typing.setAttribute('aria-label', 'Ombrail is writing');
    typing.innerHTML = '<i></i><i></i><i></i><span></span>';
    thread.append(typing);
    scrollDown();
    setBusy(true);
    // Answers that search the catalogue take several seconds: say what is happening.
    const typingText = typing.querySelector('span');
    const steps = [[2500, isProduct ? 'Checking this piece\u2019s details\u2026' : 'Looking through the collection\u2026'], [8000, 'Almost there\u2026']];
    const timers = steps.map(([ms, text]) => setTimeout(() => { typingText.textContent = text; scrollDown(); }, ms));
    try {
      const history = state.entries.slice(0, -1).filter((e) => e.role === 'user' || e.role === 'assistant').slice(-8).map((e) => ({ role: e.role, text: String(e.text || '').slice(0, 1200) }));
      const body = { message: q, history, page: pagePayload() };
      if (isProduct) body.product = productPayload();
      body.stream = true;
      const res = await fetch(endpoint + '/chat', { method: 'POST', mode: 'cors', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
      let data;
      if (res.ok && res.body && (res.headers.get('content-type') || '').includes('ndjson')) {
        data = await readStream(res, typing, typingText, timers);
      } else {
        data = await res.json().catch(() => ({}));
      }
      timers.forEach(clearTimeout);
      typing.remove();
      thread.querySelector('.o-ai__live')?.remove();
      if (!res.ok || !data.reply) {
        if (data.error === 'not_configured') aiReady = false;
        offlineReply(q, topic, data.error);
        return;
      }
      push({
        role: 'assistant', ai: true, text: data.reply,
        products: Array.isArray(data.products) ? data.products.slice(0, 3) : [],
        suggestions: Array.isArray(data.suggestions) ? data.suggestions.slice(0, 3) : [],
        handoff: data.handoff ? (data.handoff.summary || q) : '',
      });
    } catch (e) {
      timers.forEach(clearTimeout);
      typing.remove();
      offlineReply(q, topic);
    } finally {
      setBusy(false);
      if (!mobile.matches) input.focus({ preventScroll: true });
    }
  }

  /* ---------- the piece pinned at the top ---------- */
  if (cta) {
    cta.addEventListener('click', () => {
      const mode = d.priceStatus;
      if (mode === 'checkout' && d.variantId) {
        const qty = document.querySelector('[data-o-product] input[name="quantity"]')?.value || 1;
        const note = $('[data-o-ai-cta-note]');
        addToCart(d.variantId, qty, cta, note);
        return;
      }
      if (mode === 'review') { sendToTeam(''); return; }
      const primary = document.querySelector('[data-o-primary-action]');
      close();
      if (primary && primary.tagName === 'A' && typeof window.OmbrailOpenQuote === 'function') { window.OmbrailOpenQuote('standard', '', primary); return; }
      if (primary) { primary.scrollIntoView({ block: 'center' }); primary.click(); return; }
      location.assign(contactUrl(''));
    });
  }

  /* ---------- events ---------- */
  launcher.addEventListener('click', () => (isOpen() ? close() : open(launcher)));
  $('[data-o-ai-close]').addEventListener('click', close);
  scrim.addEventListener('click', close);
  $('[data-o-ai-reset]').addEventListener('click', () => {
    state = { entries: [], context: isProduct ? d.product : '' };
    save();
    renderAll();
    input.focus({ preventScroll: true });
  });
  $('[data-o-ai-team]')?.addEventListener('click', (e) => {
    if (typeof window.OmbrailOpenQuote !== 'function') return;
    e.preventDefault();
    const lastUser = [...state.entries].reverse().find((x) => x.role === 'user');
    sendToTeam(lastUser ? lastUser.text : '');
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const v = input.value;
    input.value = '';
    ask(v);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen() && !document.querySelector('dialog[open]')) close(); });

  // Any question button on the page or in the panel, and the existing "Ask" hooks.
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-o-ai-ask]');
    if (chip) { e.preventDefault(); if (!root.contains(chip)) lastOpener = chip; ask(chip.dataset.oAiAsk, chip.dataset.oAiTopic || ''); return; }
    const opener = e.target.closest('[data-o-ask], [data-o-ask-general]');
    if (opener) { e.preventDefault(); e.stopPropagation(); open(opener); }
  }, true);
  // Warm the server as soon as the visitor shows intent (hover, focus or touch on any assistant entry point).
  ['pointerover', 'focusin', 'touchstart'].forEach((type) => document.addEventListener(type, (e) => {
    if (!warmed && e.target.closest && e.target.closest('[data-o-ai-ask], [data-o-ask], [data-o-ask-general], [data-o-ai-launcher], [data-o-faq-form]')) warm();
  }, { passive: true }));
  document.querySelectorAll('[data-o-faq-form]').forEach((faqForm) => faqForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const field = faqForm.querySelector('[data-o-faq-input]');
    const v = field ? field.value : '';
    if (field) field.value = '';
    lastOpener = faqForm.querySelector('button[type="submit"]');
    ask(v);
  }));

  // Home page: keep the button out of the hero video until the visitor scrolls.
  // Phones (v23): the button steps aside while the visitor scrolls down through content (it covered prices on the right)
  // and comes back as soon as they scroll up.
  // v40: on phones the button no longer disappears while scrolling down: it shrinks to a round button and
  // shows its "Chat" label again as soon as the visitor stops scrolling or scrolls back up.
  const onHome = Boolean(document.querySelector('main[data-template="index"]'));
  var lastY = window.scrollY;
  var away = false;
  var idle = null;
  function syncTuck() {
    const y = window.scrollY;
    if (!mobile.matches || isOpen()) away = false;
    else if (y > lastY + 8) away = y > 120;
    else if (y < lastY - 8) away = false;
    if (Math.abs(y - lastY) > 8) lastY = y;
    root.classList.toggle('is-tucked', !isOpen() && onHome && y < window.innerHeight * 0.6);
    root.classList.toggle('is-compact', !isOpen() && away);
    clearTimeout(idle);
    if (away) idle = setTimeout(() => { away = false; root.classList.remove('is-compact'); }, 1400);
  }
  syncTuck();
  window.addEventListener('scroll', syncTuck, { passive: true });
  mobile.addEventListener?.('change', () => lock(isOpen()));

  /* ---------- v40: one invitation per visit ---------- */
  const teaser = $('[data-o-ai-teaser]');
  const TEASER_KEY = 'ombrail.ai.teaser';
  let teaserTimer = null;
  function hideTeaser() {
    clearTimeout(teaserTimer);
    if (teaser) teaser.hidden = true;
  }
  if (teaser) {
    let seen = '1';
    try { seen = sessionStorage.getItem(TEASER_KEY) || ''; } catch (e) { /* private mode: never show */ }
    let tries = 0;
    const tryShow = () => {
      if (isOpen() || state.entries.length) return;
      const busyNow = root.classList.contains('is-tucked') || document.querySelector('dialog[open]') ||
        (document.activeElement && document.activeElement.matches('input,textarea,select'));
      if (busyNow) { if (++tries < 30) teaserTimer = setTimeout(tryShow, 2000); return; }
      try { sessionStorage.setItem(TEASER_KEY, '1'); } catch (e) { /* ignore */ }
      teaser.hidden = false;
      teaserTimer = setTimeout(hideTeaser, 15000);
    };
    if (!seen) teaserTimer = setTimeout(tryShow, 7000);
    $('[data-o-ai-teaser-open]').addEventListener('click', () => { hideTeaser(); open(launcher); });
    $('[data-o-ai-teaser-close]').addEventListener('click', () => { hideTeaser(); launcher.focus({ preventScroll: true }); });
  }

  /* ---------- start ---------- */
  load();
  if (isProduct && d.product && state.context !== d.product) {
    if (state.entries.length) state.entries.push({ role: 'context', text: d.product });
    state.context = d.product;
    save();
  } else if (!isProduct && state.context) {
    state.context = '';
    save();
  }
  // v21 performance: the conversation is drawn when the panel first opens, not during page load.
})();
